import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import { useAdminOrCoordinatorSession, useTracker } from '../../context/TrackerContext';
import { createTask, getAllTasks, getAdminMentors, createNotice, getNotices, deleteNotice, adminUpdateTask, adminDeleteTask, adminUpdateTaskStatus } from '../../lib/api';
import type { Task, Notice, TaskAttachment } from '../../lib/api';

/* ── Animations ──────────────────────────────────────────────────────────── */
const ANIM_CSS = `
@keyframes tbFadeUp    { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
@keyframes tbPop       { 0%{transform:scale(.88);opacity:0} 60%{transform:scale(1.03)} 100%{transform:scale(1);opacity:1} }
@keyframes tbSlide     { from{opacity:0;transform:translateX(-8px)} to{opacity:1;transform:translateX(0)} }
@keyframes tbShake     { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-4px)} 40%,80%{transform:translateX(4px)} }
@keyframes tbPulse     { 0%,100%{opacity:1} 50%{opacity:.35} }
@keyframes tbSpin      { to{transform:rotate(360deg)} }
@keyframes tbStatIn    { from{opacity:0;transform:scale(.8)} to{opacity:1;transform:scale(1)} }
@keyframes tbRedPulse  { 0%,100%{box-shadow:0 0 0 0 rgba(220,38,38,.5)} 60%{box-shadow:0 0 0 8px rgba(220,38,38,0)} }
@keyframes tbGradShift { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
@keyframes tbNoticeIn  { from{opacity:0;transform:translateY(-8px) scale(.97)} to{opacity:1;transform:none} }
.tb-card       { animation: tbFadeUp .3s ease both; }
.tb-pop        { animation: tbPop .26s cubic-bezier(.34,1.56,.64,1) both; }
.tb-slide      { animation: tbSlide .2s ease both; }
.tb-urgent     { animation: tbShake 1.4s ease-in-out infinite; }
.tb-pulse      { animation: tbPulse 1.8s ease infinite; }
.tb-spin       { animation: tbSpin .7s linear infinite; }
.tb-stat       { animation: tbStatIn .35s ease both; }
.tb-red-pulse  { animation: tbRedPulse 1.8s ease-out infinite; }
.tb-notice-in  { animation: tbNoticeIn .35s ease both; }
.tb-overdue-card{ border-left:4px solid #dc2626 !important; background:linear-gradient(135deg,#fff5f5 0%,#fff 40%) !important; }
.tb-row:hover  { background:#f8f6ff; }
`;

/* ── Config ──────────────────────────────────────────────────────────────── */
const P_CFG: Record<string, { color:string; bg:string; ring:string; dot:string }> = {
  Low:    { color:'#16a34a', bg:'#f0fdf4', ring:'#86efac', dot:'#22c55e' },
  Medium: { color:'#b45309', bg:'#fefce8', ring:'#fde68a', dot:'#f59e0b' },
  High:   { color:'#c2410c', bg:'#fff7ed', ring:'#fdba74', dot:'#f97316' },
  Urgent: { color:'#dc2626', bg:'#fef2f2', ring:'#fca5a5', dot:'#ef4444' },
};
const NP_CFG: Record<string, { color:string; bg:string; ring:string; icon:string }> = {
  Info:      { color:'#2563eb', bg:'#eff6ff', ring:'#93c5fd', icon:'ℹ' },
  Important: { color:'#b45309', bg:'#fffbeb', ring:'#fde68a', icon:'⚠' },
  Urgent:    { color:'#dc2626', bg:'#fef2f2', ring:'#fca5a5', icon:'🚨' },
};
const STATUS_COLS = ['Pending','In Progress','Done','Cancelled'] as const;
const S_CFG: Record<string,{color:string;bg:string;border:string;label:string;icon:string}> = {
  Pending:       { color:'#7c3aed', bg:'#f5f3ff', border:'#ddd6fe', label:'Pending',     icon:'⏳' },
  'In Progress': { color:'#d97706', bg:'#fffbeb', border:'#fde68a', label:'In Progress', icon:'⚡' },
  Done:          { color:'#16a34a', bg:'#f0fdf4', border:'#bbf7d0', label:'Done',        icon:'✓' },
  Cancelled:     { color:'#6b7280', bg:'#f9fafb', border:'#e5e7eb', label:'Cancelled',   icon:'✕' },
};

/* ── Helpers ─────────────────────────────────────────────────────────────── */
function fmtDate(d: string) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' });
}
function daysLeft(d: string) {
  if (!d) return null;
  const diff = Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);
  return diff;
}
function isOverdue(t: Task) {
  if (!t.dueDate || t.status === 'Done' || t.status === 'Cancelled') return false;
  return new Date(t.dueDate) < new Date();
}
function fmtAgo(iso: string) {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.round(m/60)}h ago`;
  return `${Math.round(m/1440)}d ago`;
}

/* ── Kanban Task Card ─────────────────────────────────────────────────────── */
const KanbanCard: React.FC<{
  task: Task; idx: number;
  onEdit: (t:Task) => void;
  onDelete: (t:Task) => void;
  onStatusChange: (t:Task) => void;
  onDetail: (t:Task) => void;
}> = ({ task, idx, onEdit, onDelete, onStatusChange, onDetail }) => {
  const p = P_CFG[task.priority] || P_CFG.Medium;
  const s = S_CFG[task.status] || S_CFG.Pending;
  const overdue = isOverdue(task);
  const days = daysLeft(task.dueDate);
  const absDays = days !== null ? Math.abs(days) : 0;
  return (
    <div className={`tb-card rounded-xl shadow-sm cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5 ${overdue ? 'tb-overdue-card tb-red-pulse' : 'bg-white border border-gray-100'}`}
      style={{ animationDelay:`${idx*45}ms` }}
      onClick={() => onDetail(task)}>
      {/* Accent bar */}
      <div style={{ height:4, borderRadius:'12px 12px 0 0', background: overdue ? 'linear-gradient(90deg,#dc2626,#ef4444)' : task.priority==='Urgent' ? 'linear-gradient(90deg,#dc2626,#f97316)' : task.priority==='High' ? 'linear-gradient(90deg,#f97316,#fbbf24)' : task.priority==='Medium' ? 'linear-gradient(90deg,#6d28d9,#8b5cf6)' : 'linear-gradient(90deg,#16a34a,#4ade80)' }} />
      <div className="p-3.5">
        {/* Overdue banner */}
        {overdue && (
          <div className="flex items-center gap-2 mb-2.5 px-2.5 py-1.5 rounded-lg" style={{ background:'#fef2f2', border:'1px solid #fca5a5' }}>
            <span className="text-sm tb-urgent">🚨</span>
            <p className="text-xs font-black text-red-600">{absDays} DAY{absDays!==1?'S':''} OVERDUE — ACTION REQUIRED</p>
          </div>
        )}
        {/* Top row */}
        <div className="flex items-start justify-between gap-1 mb-2">
          <div className="flex items-center gap-1 flex-wrap">
            <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${task.priority==='Urgent'?'tb-urgent':''}`}
              style={{ background:p.bg, color:p.color, border:`1.5px solid ${p.ring}` }}>
              {task.priority==='Urgent'?'🔴':task.priority==='High'?'🟠':''} {task.priority}
            </span>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
              style={{ background:s.bg, color:s.color, border:`1px solid ${s.border}` }}>
              {s.icon} {s.label}
            </span>
          </div>
          <div className="flex gap-0.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
            <button onClick={() => onStatusChange(task)} title="Update Status"
              className="w-6 h-6 rounded flex items-center justify-center hover:bg-gray-100 transition-colors text-xs"
              style={{ color: s.color }}>⇄</button>
            <button onClick={() => onEdit(task)} title="Edit"
              className="w-6 h-6 rounded flex items-center justify-center text-blue-300 hover:bg-blue-50 hover:text-blue-600 transition-colors text-xs">✏</button>
            <button onClick={() => onDelete(task)} title="Delete"
              className="w-6 h-6 rounded flex items-center justify-center text-red-200 hover:bg-red-50 hover:text-red-500 transition-colors text-xs">🗑</button>
          </div>
        </div>
        {/* Title */}
        <p className="font-bold text-gray-900 text-sm leading-snug mb-1">{task.title}</p>
        {task.description && <p className="text-[11px] text-gray-500 mb-2.5 line-clamp-2 leading-relaxed">{task.description}</p>}
        {/* Meta */}
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black text-white flex-shrink-0"
              style={{ background:`hsl(${(task.assignedToName||'').charCodeAt(0)*7%360},55%,50%)` }}>
              {(task.assignedToName||'?').charAt(0)}
            </div>
            <span className="text-[11px] font-semibold text-gray-700 truncate max-w-[90px]">{task.assignedToName}</span>
          </div>
          {task.dueDate && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: overdue?'#fef2f2': days!==null&&days<=2?'#fffbeb':'#f8fafc', color: overdue?'#dc2626':days!==null&&days<=2?'#b45309':'#64748b' }}>
              📅 {days===0?'Today':days===1?'Tomorrow':fmtDate(task.dueDate)}
            </span>
          )}
        </div>
        {task.attachments && task.attachments.length > 0 && (
          <div className="flex items-center gap-1 mt-1.5">
            <span className="text-[10px] text-purple-500 font-semibold">📎 {task.attachments.length} file{task.attachments.length!==1?'s':''}</span>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── List Row ────────────────────────────────────────────────────────────── */
const ListRow: React.FC<{
  task: Task; idx: number;
  onEdit: (t:Task) => void;
  onDelete: (t:Task) => void;
  onStatusChange: (t:Task) => void;
  onDetail: (t:Task) => void;
}> = ({ task, idx, onEdit, onDelete, onStatusChange, onDetail }) => {
  const p = P_CFG[task.priority] || P_CFG.Medium;
  const s = S_CFG[task.status] || S_CFG.Pending;
  const overdue = isOverdue(task);
  const days = daysLeft(task.dueDate);
  return (
    <div className="tb-card flex items-center gap-3 bg-white rounded-xl border px-4 py-3 hover:shadow-sm transition-shadow cursor-pointer"
      style={{ animationDelay:`${idx*30}ms`, borderColor: overdue ? '#fca5a5' : '#e5e7eb', borderWidth: overdue ? 2 : 1 }}
      onClick={() => onDetail(task)}>
      {/* Priority dot */}
      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: p.dot }} />
      {/* Title + desc */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className={`font-semibold text-gray-800 text-sm ${task.priority==='Urgent'?'tb-urgent':''}`}>{task.title}</p>
          {overdue && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full tb-pulse" style={{ background:'#fef2f2', color:'#dc2626' }}>Overdue</span>}
          {task.attachments && task.attachments.length > 0 && (
            <span className="text-[10px] text-purple-500">📎 {task.attachments.length}</span>
          )}
        </div>
        {task.description && <p className="text-xs text-gray-400 truncate mt-0.5">{task.description}</p>}
      </div>
      {/* Status — clickable */}
      <button onClick={e => { e.stopPropagation(); onStatusChange(task); }} title="Update Status"
        className="hidden sm:inline text-[11px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 hover:opacity-80 transition-opacity cursor-pointer"
        style={{ background:s.bg, color:s.color, border:`1px solid ${s.border}` }}>
        {s.icon} {s.label}
      </button>
      {/* Mentor */}
      <span className="hidden md:inline text-[11px] text-gray-600 flex-shrink-0 max-w-[100px] truncate">{task.assignedToName}</span>
      {/* Due */}
      {task.dueDate ? (
        <span className="hidden lg:inline text-[11px] flex-shrink-0 font-medium"
          style={{ color: days !== null && days < 0 ? '#dc2626' : days !== null && days <= 2 ? '#d97706' : '#6b7280' }}>
          📅 {fmtDate(task.dueDate)}
        </span>
      ) : <span className="hidden lg:inline w-20" />}
      {/* Actions */}
      <div className="flex gap-0.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
        <button onClick={() => onEdit(task)} title="Edit"
          className="w-7 h-7 rounded-lg flex items-center justify-center text-blue-300 hover:bg-blue-50 hover:text-blue-600 transition-colors text-xs">✏</button>
        <button onClick={() => onDelete(task)} title="Delete"
          className="w-7 h-7 rounded-lg flex items-center justify-center text-red-200 hover:bg-red-50 hover:text-red-500 transition-colors text-xs">🗑</button>
      </div>
    </div>
  );
};

/* ── Notice Card ─────────────────────────────────────────────────────────── */
const NoticeCard: React.FC<{ notice: Notice; idx: number; onDelete: () => void }> = ({ notice, idx, onDelete }) => {
  const cfg = NP_CFG[notice.priority] || NP_CFG.Info;
  const isUrgent = notice.priority === 'Urgent';
  const isImportant = notice.priority === 'Important';
  return (
    <div className={`tb-notice-in rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all hover:-translate-y-0.5 ${isUrgent ? 'tb-red-pulse' : ''}`}
      style={{ animationDelay:`${idx*60}ms`, border:`1.5px solid ${isUrgent?'#fca5a5':isImportant?'#fde68a':'#dbeafe'}` }}>
      {/* Official header band */}
      <div style={{
        background: isUrgent
          ? 'linear-gradient(135deg,#7f1d1d,#dc2626,#ef4444)'
          : isImportant
          ? 'linear-gradient(135deg,#78350f,#d97706,#f59e0b)'
          : 'linear-gradient(135deg,#1e3a8a,#1d4ed8,#3b82f6)',
        padding:'10px 16px',
        display:'flex', alignItems:'center', justifyContent:'space-between', gap:12,
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:20 }}>{isUrgent?'🚨':isImportant?'⚠️':'📋'}</span>
          <div>
            <p style={{ fontSize:9, color:'rgba(255,255,255,.7)', fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em' }}>
              VISTA TECH · OFFICIAL NOTICE
            </p>
            <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:2 }}>
              <span style={{ fontSize:10, fontWeight:900, color:'#fff', background:'rgba(255,255,255,.2)', padding:'2px 8px', borderRadius:99, border:'1px solid rgba(255,255,255,.3)' }}>
                {notice.priority.toUpperCase()}
              </span>
              {notice.pinned && <span style={{ fontSize:10, fontWeight:700, color:'rgba(255,255,255,.85)', background:'rgba(255,255,255,.15)', padding:'2px 8px', borderRadius:99 }}>📌 PINNED</span>}
              {!notice.targetAll && <span style={{ fontSize:10, fontWeight:700, color:'rgba(255,255,255,.85)', background:'rgba(255,255,255,.15)', padding:'2px 8px', borderRadius:99 }}>🎯 TARGETED</span>}
            </div>
          </div>
        </div>
        <button onClick={onDelete} style={{ width:28, height:28, borderRadius:'50%', background:'rgba(255,255,255,.15)', border:'1px solid rgba(255,255,255,.25)', color:'rgba(255,255,255,.8)', cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>×</button>
      </div>

      {/* Body */}
      <div style={{ background:'#fff', padding:'16px' }}>
        <p style={{ fontSize:15, fontWeight:900, color:'#0f172a', lineHeight:1.3, marginBottom:8 }}>{notice.title}</p>
        <p style={{ fontSize:12, color:'#334155', lineHeight:1.7, marginBottom:12 }}>{notice.content}</p>

        {/* Footer */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', borderTop:'1px solid #f1f5f9', paddingTop:10 }}>
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <div style={{ width:22, height:22, borderRadius:'50%', background: isUrgent?'#dc2626':isImportant?'#d97706':'#2563eb', display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:900, color:'#fff' }}>
              {(notice.createdByName||'A').charAt(0)}
            </div>
            <div>
              <p style={{ fontSize:10, fontWeight:700, color:'#374151' }}>{notice.createdByName}</p>
              <p style={{ fontSize:9, color:'#94a3b8' }}>{fmtAgo(notice.createdAt)}</p>
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {notice.expiresAt && (
              <span style={{ fontSize:9, fontWeight:700, color:'#b45309', background:'#fffbeb', padding:'3px 8px', borderRadius:99, border:'1px solid #fde68a' }}>
                Expires {notice.expiresAt}
              </span>
            )}
            <div style={{ display:'flex', alignItems:'center', gap:4, background: notice.readBy.length > 0 ? '#f0fdf4':'#fef2f2', padding:'4px 10px', borderRadius:99, border:`1px solid ${notice.readBy.length>0?'#86efac':'#fca5a5'}` }}>
              <span style={{ fontSize:11 }}>{notice.readBy.length > 0 ? '✓' : '👁'}</span>
              <span style={{ fontSize:10, fontWeight:800, color: notice.readBy.length > 0 ? '#16a34a':'#dc2626' }}>
                {notice.readBy.length} read
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Stat Chip ───────────────────────────────────────────────────────────── */
const Stat: React.FC<{ label:string; value:number; color:string; bg:string; delay?:number }> = ({ label, value, color, bg, delay=0 }) => (
  <div className="tb-stat rounded-2xl px-5 py-4 flex flex-col gap-1 flex-1 min-w-[100px]"
    style={{ background:bg, animationDelay:`${delay}ms` }}>
    <p className="text-2xl font-black" style={{ color }}>{value}</p>
    <p className="text-xs font-semibold" style={{ color, opacity:.7 }}>{label}</p>
  </div>
);

/* ── Main Page ───────────────────────────────────────────────────────────── */
const ManageTasks: React.FC = () => {
  const { session } = useTracker();
  const { adminPassword } = useAdminOrCoordinatorSession();

  const [tab, setTab]             = useState<'tasks'|'notices'>('tasks');
  const [viewMode, setViewMode]   = useState<'kanban'|'list'>('list');

  /* Tasks */
  const [tasks, setTasks]         = useState<Task[]>([]);
  const [mentors, setMentors]     = useState<any[]>([]);
  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch]       = useState('');
  const [filterMentor, setFilterMentor]   = useState('');
  const [filterStatus, setFilterStatus]   = useState('');
  const [filterPriority, setFilterPriority] = useState('');

  /* Create form */
  const [showCreate, setShowCreate]         = useState(false);
  const [form, setForm]                     = useState({ title:'', description:'', priority:'Medium', dueDate:'' });
  const [selectedMentors, setSelectedMentors] = useState<string[]>([]);
  const [mentorDropOpen, setMentorDropOpen] = useState(false);
  const [attachments, setAttachments]       = useState<TaskAttachment[]>([]);
  const fileInputRef                        = useRef<HTMLInputElement>(null);
  const [creating, setCreating]             = useState(false);

  /* Edit */
  const [editTask, setEditTask]   = useState<Task|null>(null);
  const [editForm, setEditForm]   = useState({ title:'', description:'', priority:'Medium', dueDate:'' });
  const [editSaving, setEditSaving] = useState(false);

  /* Status update */
  const [statusTask, setStatusTask]   = useState<Task|null>(null);
  const [statusVal, setStatusVal]     = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [statusSaving, setStatusSaving] = useState(false);

  /* Detail drawer */
  const [detailTask, setDetailTask]       = useState<Task|null>(null);
  const [detailStatusVal, setDetailStatusVal]   = useState('');
  const [detailStatusNotes, setDetailStatusNotes] = useState('');
  const [detailStatusSaving, setDetailStatusSaving] = useState(false);
  const [lightbox, setLightbox]           = useState<{src:string;name:string}|null>(null);

  /* Notices */
  const [notices, setNotices]     = useState<Notice[]>([]);
  const [nLoading, setNLoading]   = useState(false);
  const [showNoticeForm, setShowNoticeForm] = useState(false);
  const [nForm, setNForm]         = useState({
    title:'', content:'', priority:'Info' as 'Info'|'Important'|'Urgent',
    targetAll:true, targetMentorId:'', pinned:false, expiresAt:'',
  });
  const [nCreating, setNCreating] = useState(false);

  /* ── Loaders ── */
  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const [tr, mr] = await Promise.all([getAllTasks(adminPassword), getAdminMentors(adminPassword)]);
      setTasks(tr.tasks);
      setMentors(mr.mentors as any[]);
    } catch (err: any) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [adminPassword]);

  const loadNotices = useCallback(async () => {
    setNLoading(true);
    try { const r = await getNotices(adminPassword); setNotices(r.notices); }
    catch (err: any) { toast.error(err.message); }
    finally { setNLoading(false); }
  }, [adminPassword]);

  useEffect(() => { loadTasks(); }, [loadTasks]);
  useEffect(() => { if (tab === 'notices') loadNotices(); }, [tab, loadNotices]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      if (tab === 'tasks') await loadTasks(); else await loadNotices();
    } finally { setRefreshing(false); }
  };

  /* ── File helpers ── */
  const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onload = () => res((reader.result as string).split(',')[1]);
      reader.onerror = rej;
      reader.readAsDataURL(file);
    });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const MAX = 5 * 1024 * 1024;
    const added: TaskAttachment[] = [];
    for (const f of files) {
      if (f.size > MAX) { toast.error(`${f.name} exceeds 5 MB`); continue; }
      const content = await readFileAsBase64(f);
      added.push({ name: f.name, content, type: f.type });
    }
    setAttachments(prev => [...prev, ...added]);
    e.target.value = '';
  };

  /* ── Mentor selector ── */
  const toggleMentor = (id: string) =>
    setSelectedMentors(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);


  const assignableMentors = mentors.filter(m => m.status !== 'Inactive');

  /* ── Handlers ── */
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title required'); return; }
    if (!selectedMentors.length) { toast.error('Select at least one mentor'); return; }
    setCreating(true);
    try {
      const assignedByName = session?.kind === 'admin' ? 'Admin' : 'Student Coordinator';
      const assignedBy = session?.kind === 'admin' ? 'admin' : (session as any)?.mentorId || 'admin';
      const assignedTo = selectedMentors.length === 1 ? selectedMentors[0] : selectedMentors;
      const res = await createTask(adminPassword, {
        ...form, assignedTo, assignedByName, assignedBy,
        attachments: attachments.length ? attachments : undefined,
      });
      const atStr = attachments.length ? ` with ${attachments.length} file(s)` : '';
      if ((res.assignedCount ?? 1) > 1) {
        toast.success(`Task sent to ${res.assignedCount} mentors — ${res.emailsSent} notified${atStr}`);
      } else if (res.emailSent === false) {
        toast.success('Task assigned — mentor has no email, notification skipped');
      } else {
        toast.success(`Task assigned and mentor notified${atStr}!`);
      }
      setShowCreate(false);
      setMentorDropOpen(false);
      setForm({ title:'', description:'', priority:'Medium', dueDate:'' });
      setSelectedMentors([]);
      setAttachments([]);
      loadTasks();
    } catch (err: any) { toast.error(err.message); }
    finally { setCreating(false); }
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editTask) return;
    setEditSaving(true);
    try {
      await adminUpdateTask(adminPassword, editTask.taskId, editForm);
      const priority = editForm.priority as Task['priority'];
      setTasks(prev => prev.map(t => t.taskId === editTask.taskId ? { ...t, ...editForm, priority } : t));
      toast.success('Task updated');
      setEditTask(null);
    } catch (err: any) { toast.error(err.message); }
    finally { setEditSaving(false); }
  };

  const handleDeleteTask = async (t: Task) => {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    try {
      await adminDeleteTask(adminPassword, t.taskId);
      setTasks(prev => prev.filter(x => x.taskId !== t.taskId));
      toast.success('Task deleted');
    } catch (err: any) { toast.error(err.message); }
  };

  const openStatusModal = (t: Task) => {
    setStatusTask(t);
    setStatusVal(t.status);
    setStatusNotes(t.completionNotes || '');
  };

  const openDetail = (t: Task) => {
    setDetailTask(t);
    setDetailStatusVal(t.status);
    setDetailStatusNotes(t.completionNotes || '');
  };

  const openAttachment = (a: { name: string; mimeType: string; content?: string }) => {
    if (!a.content) { toast.info('Attachment content not available for older tasks'); return; }
    const src = `data:${a.mimeType};base64,${a.content}`;
    if (a.mimeType.startsWith('image/')) {
      setLightbox({ src, name: a.name });
    } else {
      const byteChars = atob(a.content);
      const arr = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) arr[i] = byteChars.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([arr], { type: a.mimeType }));
      window.open(url, '_blank');
    }
  };

  const handleDetailStatusUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!detailTask) return;
    setDetailStatusSaving(true);
    try {
      await adminUpdateTaskStatus(adminPassword, detailTask.taskId, detailStatusVal, detailStatusNotes);
      const newEntry = {
        status: detailStatusVal,
        notes: detailStatusNotes,
        changedAt: new Date().toISOString(),
        changedBy: 'admin',
        changedByName: session?.kind === 'admin' ? 'Admin' : 'Coordinator',
      };
      const updated: Task = {
        ...detailTask,
        status: detailStatusVal as Task['status'],
        completionNotes: detailStatusNotes,
        completedAt: detailStatusVal === 'Done' ? new Date().toISOString() : null,
        history: [...(detailTask.history || []), newEntry],
      };
      setDetailTask(updated);
      setTasks(prev => prev.map(t => t.taskId === detailTask.taskId ? updated : t));
      toast.success('Status updated');
    } catch (err: any) { toast.error(err.message); }
    finally { setDetailStatusSaving(false); }
  };

  const handleStatusUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!statusTask) return;
    setStatusSaving(true);
    try {
      await adminUpdateTaskStatus(adminPassword, statusTask.taskId, statusVal, statusNotes);
      setTasks(prev => prev.map(t => t.taskId === statusTask.taskId
        ? { ...t, status: statusVal as Task['status'], completionNotes: statusNotes, completedAt: statusVal === 'Done' ? new Date().toISOString() : null }
        : t));
      toast.success('Task status updated');
      setStatusTask(null);
    } catch (err: any) { toast.error(err.message); }
    finally { setStatusSaving(false); }
  };

  const handleCreateNotice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nForm.title.trim()) { toast.error('Title required'); return; }
    if (!nForm.content.trim()) { toast.error('Content required'); return; }
    setNCreating(true);
    try {
      await createNotice(adminPassword, {
        title: nForm.title, content: nForm.content, priority: nForm.priority,
        targetAll: nForm.targetAll,
        targetMentorIds: !nForm.targetAll && nForm.targetMentorId ? [nForm.targetMentorId] : [],
        pinned: nForm.pinned, expiresAt: nForm.expiresAt || null,
      });
      toast.success('Notice posted!');
      setShowNoticeForm(false);
      setNForm({ title:'', content:'', priority:'Info', targetAll:true, targetMentorId:'', pinned:false, expiresAt:'' });
      loadNotices();
    } catch (err: any) { toast.error(err.message); }
    finally { setNCreating(false); }
  };

  const handleDeleteNotice = async (id: string) => {
    try {
      await deleteNotice(adminPassword, id);
      setNotices(prev => prev.filter(n => n.noticeId !== id));
    } catch (err: any) { toast.error(err.message); }
  };

  /* ── Derived data ── */
  const visible = tasks.filter(t => {
    if (search && !t.title.toLowerCase().includes(search.toLowerCase())
      && !t.assignedToName.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterMentor && t.assignedTo !== filterMentor) return false;
    if (filterStatus && t.status !== filterStatus) return false;
    if (filterPriority && t.priority !== filterPriority) return false;
    return true;
  });
  const byStatus = STATUS_COLS.reduce((acc, s) => {
    acc[s] = visible.filter(t => t.status === s);
    return acc;
  }, {} as Record<string, Task[]>);

  const totalTasks   = tasks.length;
  const pending      = tasks.filter(t => t.status === 'Pending').length;
  const inProgress   = tasks.filter(t => t.status === 'In Progress').length;
  const done         = tasks.filter(t => t.status === 'Done').length;
  const overdueCnt   = tasks.filter(t => isOverdue(t)).length;
  const hasFilters   = !!(search || filterMentor || filterStatus || filterPriority);

  /* ── RENDER ── */
  return (
    <TrackerLayout>
      <style>{ANIM_CSS}</style>
      <div className="space-y-5">

        {/* ── Hero Header ── */}
        <div className="rounded-2xl overflow-hidden" style={{ background:'linear-gradient(135deg,#0f0a1e 0%,#1e1433 40%,#312741 80%,#3d2f56 100%)', boxShadow:'0 16px 48px rgba(49,39,65,.5)' }}>
          {/* Overdue alert banner */}
          {overdueCnt > 0 && (
            <div className="flex items-center gap-3 px-5 py-3 tb-urgent" style={{ background:'linear-gradient(90deg,#7f1d1d,#dc2626)', borderBottom:'2px solid rgba(255,255,255,.1)' }}>
              <span className="text-xl">🚨</span>
              <div className="flex-1">
                <p className="text-white font-black text-sm">{overdueCnt} task{overdueCnt!==1?'s':''} are OVERDUE — Immediate action required!</p>
                <p className="text-red-200 text-xs font-medium">These tasks have missed their deadlines. Follow up with the assigned mentors now.</p>
              </div>
              <span className="text-3xl font-black text-white opacity-30 flex-shrink-0">{overdueCnt}</span>
            </div>
          )}
          <div className="p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-purple-300 border border-purple-500/40" style={{ background:'rgba(124,58,237,.2)' }}>ACCOUNTABILITY CENTRE</span>
                </div>
                <h1 className="text-2xl font-black text-white tracking-tight">Task & Notice Board</h1>
                <p className="text-white/50 text-sm mt-0.5">Every task assigned here is your team's responsibility — track, act, deliver.</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={handleRefresh} disabled={refreshing}
                  className="px-3 py-2 rounded-xl text-xs font-bold border border-white/20 text-white/70 hover:bg-white/10 transition-all flex items-center gap-1.5 disabled:opacity-50">
                  <span className={refreshing ? 'tb-spin inline-block' : ''}>↻</span> Refresh
                </button>
                {tab === 'tasks' ? (
                  <button onClick={() => setShowCreate(true)}
                    className="px-4 py-2 rounded-xl text-sm font-black shadow-lg hover:scale-105 active:scale-95 transition-transform"
                    style={{ background:'linear-gradient(135deg,#F0B429,#f97316)', color:'#fff', boxShadow:'0 4px 14px rgba(240,180,41,.4)' }}>
                    ＋ Assign Task
                  </button>
                ) : (
                  <button onClick={() => setShowNoticeForm(true)}
                    className="px-4 py-2 rounded-xl text-sm font-black shadow-lg hover:scale-105 active:scale-95 transition-transform"
                    style={{ background:'linear-gradient(135deg,#1d4ed8,#3b82f6)', color:'#fff', boxShadow:'0 4px 14px rgba(59,130,246,.4)' }}>
                    📢 Post Official Notice
                  </button>
                )}
              </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-5">
              {[
                { label:'Total Tasks', value:totalTasks, color:'#e9d5ff', bg:'rgba(124,58,237,.2)', icon:'📋' },
                { label:'Pending',     value:pending,    color:'#fde68a', bg:'rgba(234,179,8,.18)',  icon:'⏳' },
                { label:'In Progress', value:inProgress, color:'#fed7aa', bg:'rgba(234,88,12,.18)',  icon:'⚡' },
                { label:'Done',        value:done,       color:'#bbf7d0', bg:'rgba(22,163,74,.18)',  icon:'✅' },
                { label:'⚠ OVERDUE',  value:overdueCnt, color: overdueCnt>0?'#fca5a5':'#6b7280', bg: overdueCnt>0?'rgba(220,38,38,.25)':'rgba(107,114,128,.12)', icon:overdueCnt>0?'🔴':'—' },
              ].map((s,i) => (
                <div key={s.label} className="tb-stat rounded-xl px-4 py-3 flex items-center gap-3" style={{ background:s.bg, animationDelay:`${i*50}ms` }}>
                  <span className="text-xl flex-shrink-0">{s.icon}</span>
                  <div>
                    <p className="text-xl font-black leading-none" style={{ color:s.color }}>{s.value}</p>
                    <p className="text-[10px] font-bold mt-0.5" style={{ color:s.color, opacity:.75 }}>{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="flex gap-2">
          {([
            { id:'tasks' as const, label:'Task Board', icon:'📋', count: tasks.length, urgentCount: overdueCnt },
            { id:'notices' as const, label:'Official Notices', icon:'📢', count: notices.length, urgentCount: notices.filter(n=>n.priority==='Urgent').length },
          ]).map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-black transition-all"
              style={{
                background: tab===t.id ? 'linear-gradient(135deg,#1e1433,#312741)' : '#fff',
                color: tab===t.id ? '#fff' : '#64748b',
                boxShadow: tab===t.id ? '0 4px 14px rgba(49,39,65,.35)' : '0 1px 4px rgba(0,0,0,.07)',
                border: tab===t.id ? 'none' : '1.5px solid #e2e8f0',
              }}>
              {t.icon} {t.label}
              {t.count > 0 && (
                <span className="text-[10px] font-black px-1.5 py-0.5 rounded-full"
                  style={{ background: tab===t.id?'rgba(255,255,255,.2)':'#f1f5f9', color: tab===t.id?'#fff':'#64748b' }}>
                  {t.count}
                </span>
              )}
              {t.urgentCount > 0 && (
                <span className="text-[10px] font-black px-1.5 py-0.5 rounded-full tb-pulse"
                  style={{ background:'#dc2626', color:'#fff' }}>
                  {t.urgentCount} urgent
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Tasks Tab ── */}
        {tab === 'tasks' && (
          <>
            {/* Filter bar */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Search */}
              <div className="flex items-center gap-2 flex-1 min-w-[160px] px-3 py-2 border-2 border-gray-200 rounded-xl bg-white focus-within:border-purple-400 transition-colors">
                <span className="text-gray-400 text-sm">🔍</span>
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search tasks or mentors…"
                  className="text-sm outline-none bg-transparent flex-1 text-gray-700 placeholder-gray-400" />
                {search && <button onClick={() => setSearch('')} className="text-gray-300 hover:text-gray-500 text-xs">✕</button>}
              </div>
              <select value={filterMentor} onChange={e => setFilterMentor(e.target.value)}
                className="px-3 py-2 text-sm border-2 border-gray-200 rounded-xl outline-none focus:border-purple-400 bg-white">
                <option value="">All Mentors</option>
                {mentors.filter(m => m.status !== 'Inactive').map(m => <option key={m.mentorId} value={m.mentorId}>{m.mentorName}</option>)}
              </select>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                className="px-3 py-2 text-sm border-2 border-gray-200 rounded-xl outline-none focus:border-purple-400 bg-white">
                <option value="">All Statuses</option>
                {STATUS_COLS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}
                className="px-3 py-2 text-sm border-2 border-gray-200 rounded-xl outline-none focus:border-purple-400 bg-white">
                <option value="">All Priorities</option>
                {['Low','Medium','High','Urgent'].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              {hasFilters && (
                <button onClick={() => { setSearch(''); setFilterMentor(''); setFilterStatus(''); setFilterPriority(''); }}
                  className="text-xs text-purple-600 hover:text-purple-800 font-semibold px-2 whitespace-nowrap">
                  ✕ Clear
                </button>
              )}
              {/* View toggle */}
              <div className="flex items-center gap-1 ml-auto border-2 border-gray-200 rounded-xl p-1 bg-white">
                {(['list','kanban'] as const).map(v => (
                  <button key={v} onClick={() => setViewMode(v)}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                    style={{
                      background: viewMode===v ? '#312741' : 'transparent',
                      color: viewMode===v ? '#ffe500' : '#9ca3af',
                    }}>
                    {v === 'list' ? '☰ List' : '⊞ Board'}
                  </button>
                ))}
              </div>
            </div>

            {/* Result count */}
            {hasFilters && !loading && (
              <p className="text-xs text-gray-500 tb-slide">
                Showing <span className="font-bold text-purple-700">{visible.length}</span> of {totalTasks} tasks
              </p>
            )}

            {loading ? (
              <div className="flex flex-col items-center justify-center h-48 gap-3">
                <div className="w-8 h-8 rounded-full border-4 border-purple-200 border-t-purple-600 tb-spin" />
                <p className="text-sm text-gray-400">Loading tasks…</p>
              </div>
            ) : visible.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-5xl mb-3">✅</p>
                <p className="text-gray-500 font-semibold">{hasFilters ? 'No tasks match your filters' : 'No tasks yet'}</p>
                <p className="text-gray-400 text-sm mt-1">{hasFilters ? 'Try clearing the filters' : 'Click "Assign Task" to create the first one'}</p>
              </div>
            ) : viewMode === 'list' ? (
              /* List view */
              <div className="space-y-2">
                {/* Column headers */}
                <div className="hidden md:grid grid-cols-[auto_1fr_140px_100px_130px_72px] gap-3 px-4 py-2">
                  <div className="w-2.5" />
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">Task</p>
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">Status</p>
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">Mentor</p>
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">Due</p>
                  <div />
                </div>
                {visible.map((t, i) => <ListRow key={t.taskId} task={t} idx={i} onEdit={task => { setEditTask(task); setEditForm({ title:task.title, description:task.description||'', priority:task.priority, dueDate:task.dueDate||'' }); }} onDelete={handleDeleteTask} onStatusChange={openStatusModal} onDetail={openDetail} />)}
              </div>
            ) : (
              /* Kanban view */
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {STATUS_COLS.map(status => {
                  const cfg = S_CFG[status];
                  const col = byStatus[status] || [];
                  return (
                    <div key={status} className="rounded-2xl border-2 p-3"
                      style={{ borderColor:cfg.border, background:cfg.bg }}>
                      <div className="flex items-center justify-between mb-3 px-1">
                        <span className="text-sm font-bold flex items-center gap-1.5" style={{ color:cfg.color }}>
                          {cfg.icon} {cfg.label}
                        </span>
                        <span className="text-xs font-black px-2 py-0.5 rounded-full bg-white/80" style={{ color:cfg.color }}>{col.length}</span>
                      </div>
                      <div className="space-y-2.5">
                        {col.length === 0
                          ? <p className="text-xs text-gray-400 text-center py-6">Empty</p>
                          : col.map((t, i) => <KanbanCard key={t.taskId} task={t} idx={i}
                              onEdit={task => { setEditTask(task); setEditForm({ title:task.title, description:task.description||'', priority:task.priority, dueDate:task.dueDate||'' }); }}
                              onDelete={handleDeleteTask} onStatusChange={openStatusModal} onDetail={openDetail} />)
                        }
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ── Notices Tab ── */}
        {tab === 'notices' && (
          <>
            {nLoading ? (
              <div className="flex flex-col items-center justify-center h-40 gap-3">
                <div className="w-8 h-8 rounded-full border-4 border-blue-200 border-t-blue-600 tb-spin" />
              </div>
            ) : notices.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-5xl mb-3">📢</p>
                <p className="text-gray-500 font-semibold">No notices yet</p>
                <p className="text-gray-400 text-sm mt-1">Post an announcement for your mentor team</p>
              </div>
            ) : (
              <div className="space-y-3 max-w-2xl">
                {notices.map((n, i) => <NoticeCard key={n.noticeId} notice={n} idx={i} onDelete={() => handleDeleteNotice(n.noticeId)} />)}
              </div>
            )}
          </>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════
          MODALS
      ══════════════════════════════════════════════════════════ */}

      {/* ── Create Task Modal ── */}
      {showCreate && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-3 sm:p-6">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { setShowCreate(false); setMentorDropOpen(false); }} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg z-10 overflow-hidden max-h-[95vh] flex flex-col tb-pop">

            {/* Header */}
            <div className="px-6 py-5 flex-shrink-0" style={{ background:'linear-gradient(135deg,#1e1433,#312741)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-black text-lg">Assign New Task</p>
                  <p className="text-white/50 text-xs mt-0.5">
                    {selectedMentors.length > 0
                      ? `${selectedMentors.length} mentor(s) selected · email + attachments will be sent`
                      : 'Select mentors — they will be notified by email'}
                  </p>
                </div>
                <button onClick={() => { setShowCreate(false); setMentorDropOpen(false); }}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white/50 hover:bg-white/10 transition-colors text-lg">×</button>
              </div>
            </div>

            {/* Body */}
            <div className="overflow-y-auto p-6">
              <form onSubmit={handleCreate} className="space-y-4">

                {/* Title */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Task Title *</label>
                  <input value={form.title} onChange={e => setForm(f => ({ ...f, title:e.target.value }))} required
                    placeholder="e.g. Prepare session plan for Week 3"
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-purple-400 transition-colors" />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Description</label>
                  <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description:e.target.value }))}
                    rows={3} placeholder="Detailed instructions (optional)"
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-purple-400 resize-none transition-colors" />
                </div>

                {/* Multi-mentor dropdown */}
                <div className="relative">
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Assign To *</label>
                  <button type="button" onClick={() => setMentorDropOpen(o => !o)}
                    className="w-full px-4 py-2.5 border-2 rounded-xl text-sm text-left flex items-center justify-between transition-colors"
                    style={{ borderColor: mentorDropOpen ? '#7c3aed' : '#e5e7eb' }}>
                    <span className={selectedMentors.length === 0 ? 'text-gray-400' : 'text-gray-800 font-medium truncate pr-2'}>
                      {selectedMentors.length === 0
                        ? '— Select mentors —'
                        : selectedMentors.length === assignableMentors.length && assignableMentors.length > 0
                          ? '🌐 All Active Mentors'
                          : selectedMentors.map(id => mentors.find(x => x.mentorId === id)?.mentorName || id).join(', ')}
                    </span>
                    <span className="text-gray-400 text-xs flex-shrink-0">{mentorDropOpen ? '▲' : '▼'}</span>
                  </button>

                  {mentorDropOpen && (
                    <div className="absolute z-20 w-full mt-1 bg-white border-2 border-purple-200 rounded-2xl shadow-2xl max-h-52 overflow-y-auto">
                      <label className="flex items-center gap-3 px-4 py-3 hover:bg-purple-50 cursor-pointer border-b border-gray-100">
                        <input type="checkbox"
                          checked={selectedMentors.length === assignableMentors.length && assignableMentors.length > 0}
                          onChange={e => setSelectedMentors(e.target.checked ? assignableMentors.map(m => m.mentorId) : [])}
                          className="w-4 h-4 accent-purple-600" />
                        <span className="text-sm font-bold text-purple-700">🌐 Select All Active Mentors</span>
                        <span className="ml-auto text-[11px] text-gray-400 font-medium">{assignableMentors.length} total</span>
                      </label>
                      {assignableMentors.map(m => (
                        <label key={m.mentorId} className="flex items-center gap-3 px-4 py-2.5 hover:bg-purple-50 cursor-pointer">
                          <input type="checkbox"
                            checked={selectedMentors.includes(m.mentorId)}
                            onChange={() => toggleMentor(m.mentorId)}
                            className="w-4 h-4 accent-purple-600" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-700 font-medium truncate">{m.mentorName}</p>
                            {!m.email && <p className="text-[10px] text-orange-400">⚠ No email set</p>}
                          </div>
                          {m.role && m.role !== 'Standard' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold flex-shrink-0"
                              style={{ background:'#ede9fe', color:'#7c3aed' }}>{m.role}</span>
                          )}
                        </label>
                      ))}
                      {assignableMentors.length === 0 && (
                        <p className="text-sm text-gray-400 px-4 py-3 text-center">No mentors available</p>
                      )}
                    </div>
                  )}

                  {selectedMentors.length > 0 && (
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-[11px] font-bold text-purple-600">{selectedMentors.length} selected</span>
                      <button type="button" onClick={() => setSelectedMentors([])}
                        className="text-[11px] text-gray-400 hover:text-red-400 transition-colors">✕ clear</button>
                    </div>
                  )}
                </div>

                {/* Priority + Due */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Priority</label>
                    <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority:e.target.value }))}
                      className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-purple-400">
                      {['Low','Medium','High','Urgent'].map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Due Date</label>
                    <input type="date" value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate:e.target.value }))}
                      min={new Date().toISOString().slice(0,10)}
                      className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-purple-400" />
                  </div>
                </div>

                {/* Attachments */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Attachments</label>
                  <div onClick={() => fileInputRef.current?.click()}
                    className="w-full border-2 border-dashed border-gray-300 rounded-2xl p-4 text-center cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors">
                    <p className="text-2xl mb-1">📎</p>
                    <p className="text-sm text-gray-600 font-semibold">Click to attach files</p>
                    <p className="text-xs text-gray-400 mt-0.5">Images (JPG, PNG) or PDF · max 5 MB each</p>
                  </div>
                  <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf" className="hidden" onChange={handleFileChange} />
                  {attachments.length > 0 && (
                    <div className="mt-2 space-y-1.5">
                      {attachments.map((a, i) => (
                        <div key={i} className="flex items-center gap-2 bg-purple-50 border border-purple-100 rounded-xl px-3 py-2">
                          <span>{a.type.startsWith('image') ? '🖼' : '📄'}</span>
                          <span className="text-xs text-gray-700 font-medium flex-1 truncate">{a.name}</span>
                          <button type="button" onClick={() => setAttachments(p => p.filter((_,j) => j!==i))}
                            className="text-gray-300 hover:text-red-400 transition-colors text-sm">✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {form.priority === 'Urgent' && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-700 font-semibold tb-slide">
                    🚨 Urgent tasks are highlighted with a shake animation and marked prominently.
                  </div>
                )}

                <div className="flex gap-3 pt-1">
                  <button type="button" onClick={() => { setShowCreate(false); setMentorDropOpen(false); }}
                    className="flex-1 py-3 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">
                    Cancel
                  </button>
                  <button type="submit" disabled={creating}
                    className="flex-1 py-3 rounded-xl text-sm font-black text-white disabled:opacity-60 transition-opacity"
                    style={{ background:'linear-gradient(135deg,#312741,#5a4a6f)' }}>
                    {creating ? '⟳ Assigning…' : selectedMentors.length > 1 ? `Assign to ${selectedMentors.length} Mentors` : 'Assign Task'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Task Modal ── */}
      {editTask && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-3 sm:p-6">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setEditTask(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md z-10 overflow-hidden max-h-[94vh] flex flex-col tb-pop">
            <div className="px-6 py-4 flex-shrink-0" style={{ background:'linear-gradient(135deg,#1e40af,#3b82f6)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-black text-base">Edit Task</p>
                  <p className="text-white/60 text-xs mt-0.5 truncate">For: {editTask.assignedToName}</p>
                </div>
                <button onClick={() => setEditTask(null)} className="w-8 h-8 flex items-center justify-center text-white/50 hover:bg-white/10 rounded-full text-lg">×</button>
              </div>
            </div>
            <form onSubmit={handleEditSave} className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Task Title *</label>
                <input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title:e.target.value }))} required
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-blue-400" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Description</label>
                <textarea value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description:e.target.value }))}
                  rows={3} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-blue-400 resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Priority</label>
                  <select value={editForm.priority} onChange={e => setEditForm(f => ({ ...f, priority:e.target.value }))}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-blue-400">
                    {['Low','Medium','High','Urgent'].map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Due Date</label>
                  <input type="date" value={editForm.dueDate} onChange={e => setEditForm(f => ({ ...f, dueDate:e.target.value }))}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-blue-400" />
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setEditTask(null)}
                  className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={editSaving}
                  className="flex-1 py-2.5 rounded-xl text-sm font-black text-white disabled:opacity-60"
                  style={{ background:'linear-gradient(135deg,#1e40af,#3b82f6)' }}>
                  {editSaving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Post Notice Modal ── */}
      {showNoticeForm && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-3 sm:p-6">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowNoticeForm(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md z-10 overflow-hidden max-h-[94vh] flex flex-col tb-pop">
            <div className="px-6 py-4 flex-shrink-0" style={{ background:'linear-gradient(135deg,#1e3a8a,#3b82f6)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-black text-base">📢 Post Notice</p>
                  <p className="text-white/60 text-xs mt-0.5">Mentors see this in their Quick Actions</p>
                </div>
                <button onClick={() => setShowNoticeForm(false)} className="w-8 h-8 flex items-center justify-center text-white/50 hover:bg-white/10 rounded-full text-lg">×</button>
              </div>
            </div>
            <div className="overflow-y-auto p-6">
              <form onSubmit={handleCreateNotice} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Title *</label>
                  <input value={nForm.title} onChange={e => setNForm(f => ({ ...f, title:e.target.value }))} required
                    placeholder="e.g. Class schedule update for Diwali week"
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-blue-400" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Content *</label>
                  <textarea value={nForm.content} onChange={e => setNForm(f => ({ ...f, content:e.target.value }))} required
                    rows={4} placeholder="Full notice message…"
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-blue-400 resize-none" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Priority</label>
                    <select value={nForm.priority} onChange={e => setNForm(f => ({ ...f, priority:e.target.value as any }))}
                      className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-blue-400">
                      {['Info','Important','Urgent'].map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Expires</label>
                    <input type="date" value={nForm.expiresAt} onChange={e => setNForm(f => ({ ...f, expiresAt:e.target.value }))}
                      min={new Date().toISOString().slice(0,10)}
                      className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-blue-400" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Audience</label>
                  <div className="flex gap-2">
                    {([true,false] as const).map(all => (
                      <button key={String(all)} type="button" onClick={() => setNForm(f => ({ ...f, targetAll:all }))}
                        className="flex-1 py-2 rounded-xl text-xs font-bold border-2 transition-all"
                        style={{
                          borderColor: nForm.targetAll===all ? '#3b82f6' : '#e5e7eb',
                          background: nForm.targetAll===all ? '#eff6ff' : '#fff',
                          color: nForm.targetAll===all ? '#2563eb' : '#6b7280',
                        }}>
                        {all ? '🌐 All Mentors' : '🎯 Specific'}
                      </button>
                    ))}
                  </div>
                  {!nForm.targetAll && (
                    <select value={nForm.targetMentorId} onChange={e => setNForm(f => ({ ...f, targetMentorId:e.target.value }))}
                      className="mt-2 w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-blue-400">
                      <option value="">— Select mentor —</option>
                      {mentors
                        .filter(m => m.status !== 'Inactive')
                        .map(m => <option key={m.mentorId} value={m.mentorId}>{m.mentorName}</option>)}
                    </select>
                  )}
                </div>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" checked={nForm.pinned} onChange={e => setNForm(f => ({ ...f, pinned:e.target.checked }))}
                    className="w-4 h-4 rounded accent-blue-600" />
                  <span className="text-sm text-gray-700 font-medium">📌 Pin at top</span>
                </label>
                <div className="flex gap-3 pt-1">
                  <button type="button" onClick={() => setShowNoticeForm(false)}
                    className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
                  <button type="submit" disabled={nCreating}
                    className="flex-1 py-2.5 rounded-xl text-sm font-black text-white disabled:opacity-60"
                    style={{ background:'linear-gradient(135deg,#1e3a8a,#3b82f6)' }}>
                    {nCreating ? 'Posting…' : 'Post Notice'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {/* ── Task Detail Modal ── */}
      {detailTask && (() => {
        const p = P_CFG[detailTask.priority] || P_CFG.Medium;
        const s = S_CFG[detailTask.status] || S_CFG.Pending;
        const overdue = isOverdue(detailTask);
        const days = daysLeft(detailTask.dueDate);
        const hist = [...(detailTask.history || [])].reverse();
        return (
          <div className="fixed inset-0 z-[400] flex items-center justify-center p-3 sm:p-5">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDetailTask(null)} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg z-10 overflow-hidden max-h-[95vh] flex flex-col tb-pop">
              {/* Header */}
              <div className="px-5 py-4 flex-shrink-0 sticky top-0 z-10" style={{ background:'linear-gradient(135deg,#1e1433,#312741)' }}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${detailTask.priority==='Urgent'?'tb-urgent':''}`}
                        style={{ background:p.bg, color:p.color }}>
                        {detailTask.priority}
                      </span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                        style={{ background:s.bg, color:s.color }}>
                        {s.icon} {s.label}
                      </span>
                      {overdue && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full tb-pulse" style={{ background:'#fef2f2', color:'#dc2626' }}>Overdue</span>}
                    </div>
                    <h2 className="text-white font-black text-base leading-snug">{detailTask.title}</h2>
                  </div>
                  <button onClick={() => setDetailTask(null)} className="w-8 h-8 flex-shrink-0 flex items-center justify-center text-white/50 hover:bg-white/10 rounded-full text-lg mt-0.5">×</button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
                {/* Description */}
                {detailTask.description && (
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">Description</p>
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{detailTask.description}</p>
                  </div>
                )}

                {/* Meta grid */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-0.5">Assigned To</p>
                    <p className="text-sm font-semibold text-gray-800">{detailTask.assignedToName}</p>
                    {detailTask.assignedToEmail && <p className="text-[11px] text-gray-400 truncate">{detailTask.assignedToEmail}</p>}
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-0.5">Assigned By</p>
                    <p className="text-sm font-semibold text-gray-800">{detailTask.assignedByName}</p>
                    <p className="text-[11px] text-gray-400">{new Date(detailTask.createdAt).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</p>
                  </div>
                  {detailTask.dueDate && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-0.5">Due Date</p>
                      <p className="text-sm font-semibold" style={{ color: days !== null && days < 0 ? '#dc2626' : days !== null && days <= 2 ? '#d97706' : '#374151' }}>
                        {fmtDate(detailTask.dueDate)}
                      </p>
                      {days !== null && <p className="text-[11px]" style={{ color: days < 0 ? '#dc2626' : '#6b7280' }}>{days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'Due today' : `${days}d left`}</p>}
                    </div>
                  )}
                  {detailTask.completedAt && (
                    <div className="bg-green-50 rounded-xl p-3">
                      <p className="text-[10px] font-bold text-green-500 uppercase tracking-wide mb-0.5">Completed</p>
                      <p className="text-sm font-semibold text-green-700">{new Date(detailTask.completedAt).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</p>
                    </div>
                  )}
                </div>

                {/* Completion notes */}
                {detailTask.completionNotes && (
                  <div className="bg-green-50 border border-green-100 rounded-xl p-3">
                    <p className="text-[10px] font-bold text-green-500 uppercase tracking-wide mb-1">Completion Notes</p>
                    <p className="text-sm text-green-800">{detailTask.completionNotes}</p>
                  </div>
                )}

                {/* Attachments */}
                {detailTask.attachments && detailTask.attachments.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Attachments</p>
                    <div className="space-y-2">
                      {detailTask.attachments.map((a, i) => (
                        <div key={i}>
                          {a.mimeType?.startsWith('image/') && a.content ? (
                            <button type="button" onClick={() => openAttachment(a)}
                              className="w-full rounded-xl overflow-hidden border-2 border-purple-100 hover:border-purple-400 transition-colors group relative">
                              <img src={`data:${a.mimeType};base64,${a.content}`} alt={a.name}
                                className="w-full max-h-48 object-contain bg-gray-50" />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                <span className="opacity-0 group-hover:opacity-100 text-white font-bold text-sm bg-black/50 px-3 py-1 rounded-full transition-opacity">🔍 View Full</span>
                              </div>
                              <p className="text-[11px] text-gray-500 px-3 py-1.5 text-left truncate">{a.name}</p>
                            </button>
                          ) : (
                            <button type="button" onClick={() => openAttachment(a)}
                              className="w-full flex items-center gap-2 bg-purple-50 border border-purple-100 hover:border-purple-400 hover:bg-purple-100 rounded-xl px-3 py-2.5 transition-colors text-left">
                              <span className="text-lg">📄</span>
                              <span className="text-xs text-gray-700 font-medium flex-1 truncate">{a.name}</span>
                              <span className="text-[10px] text-purple-500 font-semibold flex-shrink-0">{a.content ? 'Open ↗' : 'No preview'}</span>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Status Update Form ── */}
                <div className="border-t-2 border-gray-100 pt-4">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Update Status</p>
                  <form onSubmit={handleDetailStatusUpdate} className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      {STATUS_COLS.map(st => {
                        const cfg = S_CFG[st];
                        return (
                          <button key={st} type="button" onClick={() => setDetailStatusVal(st)}
                            className="py-2.5 rounded-xl text-xs font-bold border-2 transition-all"
                            style={{
                              borderColor: detailStatusVal === st ? cfg.color : '#e5e7eb',
                              background: detailStatusVal === st ? cfg.bg : '#fff',
                              color: detailStatusVal === st ? cfg.color : '#9ca3af',
                            }}>
                            {cfg.icon} {cfg.label}
                          </button>
                        );
                      })}
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Notes / Comments</label>
                      <textarea value={detailStatusNotes} onChange={e => setDetailStatusNotes(e.target.value)}
                        rows={3} placeholder={detailStatusVal === 'Done' ? 'Describe what was completed…' : 'Add a note about this status change (optional)'}
                        className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-purple-400 resize-none transition-colors" />
                    </div>
                    <button type="submit" disabled={detailStatusSaving || detailStatusVal === detailTask.status && detailStatusNotes === (detailTask.completionNotes || '')}
                      className="w-full py-2.5 rounded-xl text-sm font-black text-white disabled:opacity-50 transition-opacity"
                      style={{ background:'linear-gradient(135deg,#312741,#5a4a6f)' }}>
                      {detailStatusSaving ? '⟳ Saving…' : 'Save Status Update'}
                    </button>
                  </form>
                </div>

                {/* ── Timeline ── */}
                <div className="border-t-2 border-gray-100 pt-4 pb-6">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-4">Timeline</p>
                  {hist.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">No history yet</p>
                  ) : (
                    <div className="relative">
                      <div className="absolute left-3.5 top-0 bottom-0 w-0.5 bg-gray-200" />
                      <div className="space-y-4">
                        {hist.map((h, i) => {
                          const hCfg = S_CFG[h.status] || S_CFG.Pending;
                          return (
                            <div key={i} className="flex gap-3 relative tb-slide" style={{ animationDelay:`${i*40}ms` }}>
                              <div className="w-7 h-7 rounded-full border-2 bg-white flex items-center justify-center text-xs flex-shrink-0 z-10"
                                style={{ borderColor: hCfg.color, color: hCfg.color }}>
                                {hCfg.icon}
                              </div>
                              <div className="flex-1 min-w-0 pt-0.5">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-xs font-bold" style={{ color: hCfg.color }}>{hCfg.label}</span>
                                  <span className="text-[10px] text-gray-400">by {h.changedByName}</span>
                                </div>
                                {h.notes && <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{h.notes}</p>}
                                <p className="text-[10px] text-gray-400 mt-1">{new Date(h.changedAt).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})} · {new Date(h.changedAt).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Image Lightbox ── */}
      {lightbox && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-4 bg-black/90" onClick={() => setLightbox(null)}>
          <div className="relative max-w-full max-h-full flex flex-col items-center gap-3">
            <button onClick={() => setLightbox(null)} className="absolute -top-10 right-0 text-white/70 hover:text-white text-3xl leading-none">×</button>
            <img src={lightbox.src} alt={lightbox.name} className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl" onClick={e => e.stopPropagation()} />
            <p className="text-white/60 text-sm">{lightbox.name}</p>
          </div>
        </div>
      )}

      {/* ── Status Update Modal ── */}
      {statusTask && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-3 sm:p-6">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setStatusTask(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm z-10 overflow-hidden tb-pop">
            <div className="px-6 py-4" style={{ background:'linear-gradient(135deg,#14532d,#16a34a)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-black text-base">Update Status</p>
                  <p className="text-white/60 text-xs mt-0.5 truncate max-w-[220px]">{statusTask.title}</p>
                </div>
                <button onClick={() => setStatusTask(null)} className="w-8 h-8 flex items-center justify-center text-white/50 hover:bg-white/10 rounded-full text-lg">×</button>
              </div>
            </div>
            <form onSubmit={handleStatusUpdate} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">New Status</label>
                <div className="grid grid-cols-2 gap-2">
                  {STATUS_COLS.map(st => {
                    const cfg = S_CFG[st];
                    return (
                      <button key={st} type="button" onClick={() => setStatusVal(st)}
                        className="py-2.5 rounded-xl text-xs font-bold border-2 transition-all"
                        style={{
                          borderColor: statusVal === st ? cfg.color : '#e5e7eb',
                          background: statusVal === st ? cfg.bg : '#fff',
                          color: statusVal === st ? cfg.color : '#9ca3af',
                        }}>
                        {cfg.icon} {cfg.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {statusVal === 'Done' && (
                <div className="tb-slide">
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Completion Notes</label>
                  <textarea value={statusNotes} onChange={e => setStatusNotes(e.target.value)}
                    rows={3} placeholder="Add notes about the completion (optional)"
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-green-400 resize-none transition-colors" />
                </div>
              )}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setStatusTask(null)}
                  className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={statusSaving}
                  className="flex-1 py-2.5 rounded-xl text-sm font-black text-white disabled:opacity-60"
                  style={{ background:'linear-gradient(135deg,#14532d,#16a34a)' }}>
                  {statusSaving ? 'Saving…' : 'Update Status'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </TrackerLayout>
  );
};

export default ManageTasks;
